import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getApprovalRequests, approveCredential, rejectCredential } from '../services/api';
import { CredentialApprovalRequest } from '../types';
import './AdminDashboard.css';

interface User {
  userId: string;
  email: string;
  username?: string;
  role: 'NORMAL_USER' | 'ADMIN' | 'SUPER_ADMIN';
  createdAt: string;
  updatedAt: string;
}

interface ListUsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

type AdminTab = 'credentials' | 'users';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  // Tab State
  const [activeTab, setActiveTab] = useState<AdminTab>('credentials');

  // User Management State
  const [usersLoading, setUsersLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize] = useState(10);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<'NORMAL_USER' | 'ADMIN' | 'SUPER_ADMIN'>('NORMAL_USER');

  // Credential Approval State
  const [credLoading, setCredLoading] = useState(true);
  const [requests, setRequests] = useState<CredentialApprovalRequest[]>([]);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [processingCredId, setProcessingCredId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState<{ [key: string]: string }>({});
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);

  // Shared State
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const API_BASE = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:3000' 
    : 'https://0x902r3pk4.execute-api.ap-south-1.amazonaws.com/dev';

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Check admin access and redirect if not admin
  useEffect(() => {
    console.log('AdminDashboard - isAuthenticated:', isAuthenticated);
    console.log('AdminDashboard - user:', user);
    console.log('AdminDashboard - user?.username:', user?.username);
    
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    // Only allow admins to access this page
    if (user?.username !== 'subham11@gmail.com') {
      console.log('Not super admin, redirecting to profile. Username:', user?.username);
      navigate('/profile');
      return;
    }
    
    console.log('Access granted to admin dashboard');
  }, [isAuthenticated, user, navigate]);

  // Fetch users list
  const fetchUsers = useCallback(async (email?: string, page: number = 1) => {
    setUsersLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const offset = (page - 1) * pageSize;
      let url = `${API_BASE}/admin/users?limit=${pageSize}&offset=${offset}`;

      if (email && email.trim()) {
        url = `${API_BASE}/admin/users/search?email=${encodeURIComponent(email)}&limit=${pageSize}&offset=${offset}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data: ListUsersResponse = await response.json();
      setUsers(data.users);
      setTotalUsers(data.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [pageSize]);

  // Credential Approval Functions
  const fetchRequests = useCallback(async () => {
    setCredLoading(true);
    setError(null);
    try {
      const data = await getApprovalRequests(filter === 'all' ? undefined : filter);
      setRequests(data);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load approval requests');
    } finally {
      setCredLoading(false);
    }
  }, [filter]);

  const handleApprove = async (requestId: string) => {
    setProcessingCredId(requestId);
    setError(null);
    try {
      await approveCredential(requestId, 'Credentials verified and approved.');
      setSuccessMessage('Credential approved successfully!');
      fetchRequests();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error approving credential:', err);
      setError('Failed to approve credential');
    } finally {
      setProcessingCredId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    const notes = rejectNotes[requestId] || 'Credentials could not be verified.';
    setProcessingCredId(requestId);
    setError(null);
    try {
      await rejectCredential(requestId, notes);
      setSuccessMessage('Credential rejected.');
      setShowRejectModal(null);
      setRejectNotes(prev => ({ ...prev, [requestId]: '' }));
      fetchRequests();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error rejecting credential:', err);
      setError('Failed to reject credential');
    } finally {
      setProcessingCredId(null);
    }
  };

  // Initial load
  useEffect(() => {
    if (activeTab === 'credentials') {
      fetchRequests();
    } else {
      fetchUsers();
    }
  }, [activeTab, fetchUsers, fetchRequests]);

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setSearchEmail(email);
    setCurrentPage(1);
    fetchUsers(email, 1);
  };

  // Handle role update
  const handleUpdateRole = async () => {
    if (!showRoleModal) return;

    setProcessingUserId(showRoleModal.userId);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE}/admin/users/${showRoleModal.userId}/role`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: selectedRole }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update role: ${response.statusText}`);
      }

      setSuccessMessage(`User role updated to ${selectedRole} successfully!`);
      setShowRoleModal(null);
      fetchUsers(searchEmail, currentPage);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setProcessingUserId(null);
    }
  };

  // Open role change modal
  const openRoleModal = (userItem: User) => {
    setShowRoleModal(userItem);
    setSelectedRole(userItem.role);
  };

  // Helper functions for credential approval
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'badge-pending';
      case 'approved': return 'badge-approved';
      case 'rejected': return 'badge-rejected';
      default: return '';
    }
  };

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    total: requests.length
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalUsers / pageSize);
  const startIdx = (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, totalUsers);

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <div className="header-content">
            <h1>� Admin Dashboard</h1>
            <p className="admin-subtitle">Credential Verification & User Management</p>
          </div>
          <div className="admin-user-info">
            <span className="admin-badge">👑 Super Admin</span>
            <span className="admin-email">{user?.email}</span>
          </div>
        </div>

        {/* Messages */}
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'credentials' ? 'active' : ''}`}
            onClick={() => setActiveTab('credentials')}
          >
            📜 Credential Approval
          </button>
          <button 
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 User Management
          </button>
        </div>

        {/* CREDENTIAL APPROVAL TAB */}
        {activeTab === 'credentials' && (
          <>
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card stat-pending">
                <div className="stat-icon">⏳</div>
                <div className="stat-info">
                  <span className="stat-number">{stats.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
              <div className="stat-card stat-approved">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <span className="stat-number">{stats.approved}</span>
                  <span className="stat-label">Approved</span>
                </div>
              </div>
              <div className="stat-card stat-rejected">
                <div className="stat-icon">❌</div>
                <div className="stat-info">
                  <span className="stat-number">{stats.rejected}</span>
                  <span className="stat-label">Rejected</span>
                </div>
              </div>
              <div className="stat-card stat-total">
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <span className="stat-number">{stats.total}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
              >
                Pending ({stats.pending})
              </button>
              <button
                className={`filter-tab ${filter === 'approved' ? 'active' : ''}`}
                onClick={() => setFilter('approved')}
              >
                Approved ({stats.approved})
              </button>
              <button
                className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`}
                onClick={() => setFilter('rejected')}
              >
                Rejected ({stats.rejected})
              </button>
              <button
                className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All ({stats.total})
              </button>
            </div>

            {/* Requests List */}
            <div className="requests-container">
              {credLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading requests...</p>
                </div>
              ) : requests.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No {filter !== 'all' ? filter : ''} requests</h3>
                  <p>There are no credential approval requests to display.</p>
                </div>
              ) : (
                <div className="requests-list">
                  {requests.map((request) => (
                    <div key={request.requestId} className={`request-card status-${request.status}`}>
                      <div className="request-header">
                        <div className="user-info">
                          <span className="user-email">{request.userEmail}</span>
                          <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </div>
                        <span className="request-date">{formatDate(request.submittedAt)}</span>
                      </div>

                      <div className="request-body">
                        <div className="professional-info">
                          <span className="prof-type-badge">{request.professionalType}</span>
                          {request.specialties && request.specialties.length > 0 && (
                            <div className="specialties-list">
                              {request.specialties.map((spec, idx) => (
                                <span key={idx} className="specialty-tag">{spec}</span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="certification-info">
                          <h4>📜 {request.certificationName}</h4>
                          <p><strong>Issuing Org:</strong> {request.issuingOrganization}</p>
                          <p><strong>Issue Date:</strong> {request.issueDate}</p>
                          {request.expiryDate && <p><strong>Expiry:</strong> {request.expiryDate}</p>}
                        </div>

                        <div className="document-section">
                          <a
                            href={request.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-document-btn"
                          >
                            📄 View Document
                          </a>
                        </div>

                        {request.status === 'approved' && request.approvedAt && (
                          <div className="approval-info">
                            <p>✅ Approved on {formatDate(request.approvedAt)}</p>
                            {request.adminNotes && <p className="admin-notes">{request.adminNotes}</p>}
                          </div>
                        )}

                        {request.status === 'rejected' && request.rejectedAt && (
                          <div className="rejection-info">
                            <p>❌ Rejected on {formatDate(request.rejectedAt)}</p>
                            {request.adminNotes && (
                              <p className="rejection-reason">
                                <strong>Reason:</strong> {request.adminNotes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {request.status === 'pending' && (
                        <div className="request-actions">
                          <button
                            className="approve-btn"
                            onClick={() => handleApprove(request.requestId)}
                            disabled={processingCredId === request.requestId}
                          >
                            {processingCredId === request.requestId ? 'Processing...' : '✅ Approve'}
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => setShowRejectModal(request.requestId)}
                            disabled={processingCredId === request.requestId}
                          >
                            ❌ Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* USER MANAGEMENT TAB */}
        {activeTab === 'users' && (
          <>
        {/* Search Section */}
        <div className="search-section">
          <div className="search-box">
            <input
              type="email"
              placeholder="🔍 Search by email..."
              value={searchEmail}
              onChange={handleSearch}
              className="search-input"
            />
            {searchEmail && (
              <button
                className="clear-search-btn"
                onClick={() => {
                  setSearchEmail('');
                  setCurrentPage(1);
                  fetchUsers('', 1);
                }}
              >
                ✕
              </button>
            )}
          </div>
          <div className="search-info">
            {searchEmail ? (
              <p>Showing {totalUsers} result(s) for "{searchEmail}"</p>
            ) : (
              <p>Total users: {totalUsers}</p>
            )}
          </div>
        </div>

        {/* Users List */}
        <div className="users-container">
          {usersLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>No users found</h3>
              <p>
                {searchEmail
                  ? `No users match the email "${searchEmail}"`
                  : 'No users available'}
              </p>
            </div>
          ) : (
            <>
              <div className="users-table-wrapper">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Username</th>
                      <th>Current Role</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userItem) => (
                      <tr key={userItem.userId} className={`user-row role-${(userItem.role || 'normal_user').toLowerCase()}`}>
                        <td className="email-cell">
                          <div className="email-display">
                            <span className="email">{userItem.email}</span>
                            {userItem.email === 'subham11@gmail.com' && (
                              <span className="you-badge">You</span>
                            )}
                          </div>
                        </td>
                        <td className="username-cell">
                          {userItem.username || '—'}
                        </td>
                        <td className="role-cell">
                          <span className={`role-badge role-${(userItem.role || 'normal_user').toLowerCase()}`}>
                            {userItem.role === 'NORMAL_USER' && '👤'}
                            {userItem.role === 'ADMIN' && '🔑'}
                            {userItem.role === 'SUPER_ADMIN' && '👑'}
                            {!userItem.role && '👤'}
                            {' ' + (userItem.role || 'NORMAL_USER').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="date-cell">
                          {new Date(userItem.createdAt).toLocaleDateString('en-IN')}
                        </td>
                        <td className="actions-cell">
                          {userItem.email !== 'subham11@gmail.com' ? (
                            <button
                              className="change-role-btn"
                              onClick={() => openRoleModal(userItem)}
                              disabled={processingUserId === userItem.userId}
                            >
                              {processingUserId === userItem.userId ? '⏳' : '⚙️'} Change Role
                            </button>
                          ) : (
                            <span className="self-user">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-section">
                <div className="pagination-info">
                  Showing {startIdx} to {endIdx} of {totalUsers} users
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => fetchUsers(searchEmail, currentPage - 1)}
                    disabled={currentPage === 1 || usersLoading}
                  >
                    ← Previous
                  </button>
                  <div className="page-indicator">
                    Page {currentPage} of {totalPages || 1}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => fetchUsers(searchEmail, currentPage + 1)}
                    disabled={currentPage >= totalPages || usersLoading}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
          </>
        )}

        {/* Role Change Modal */}
        {showRoleModal && (
          <div className="modal-overlay" onClick={() => setShowRoleModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Change User Role</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowRoleModal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="user-info-block">
                  <p><strong>Email:</strong> {showRoleModal.email}</p>
                  <p><strong>Current Role:</strong> <span className={`role-badge role-${showRoleModal.role.toLowerCase()}`}>{showRoleModal.role}</span></p>
                </div>

                <div className="role-selector">
                  <label>Select New Role:</label>
                  <div className="role-options">
                    <label className="role-option">
                      <input
                        type="radio"
                        name="role"
                        value="NORMAL_USER"
                        checked={selectedRole === 'NORMAL_USER'}
                        onChange={(e) => setSelectedRole(e.target.value as any)}
                      />
                      <span className="option-content">
                        <span className="option-icon">👤</span>
                        <span>
                          <strong>Normal User</strong>
                          <small>Limited access, 5 questions per hour</small>
                        </span>
                      </span>
                    </label>

                    <label className="role-option">
                      <input
                        type="radio"
                        name="role"
                        value="ADMIN"
                        checked={selectedRole === 'ADMIN'}
                        onChange={(e) => setSelectedRole(e.target.value as any)}
                      />
                      <span className="option-content">
                        <span className="option-icon">🔑</span>
                        <span>
                          <strong>Admin</strong>
                          <small>Can manage some features</small>
                        </span>
                      </span>
                    </label>

                    <label className="role-option">
                      <input
                        type="radio"
                        name="role"
                        value="SUPER_ADMIN"
                        checked={selectedRole === 'SUPER_ADMIN'}
                        onChange={(e) => setSelectedRole(e.target.value as any)}
                      />
                      <span className="option-content">
                        <span className="option-icon">👑</span>
                        <span>
                          <strong>Super Admin</strong>
                          <small>Full access, unlimited questions</small>
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {selectedRole !== showRoleModal.role && (
                  <div className="warning-message">
                    ⚠️ You are about to change this user's role from <strong>{showRoleModal.role}</strong> to <strong>{selectedRole}</strong>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowRoleModal(null)}
                  disabled={processingUserId === showRoleModal.userId}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn"
                  onClick={handleUpdateRole}
                  disabled={processingUserId === showRoleModal.userId || selectedRole === showRoleModal.role}
                >
                  {processingUserId === showRoleModal.userId ? '⏳ Updating...' : '✅ Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Credential Modal */}
        {showRejectModal && (
          <div className="modal-overlay" onClick={() => setShowRejectModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Reject Credential</h3>
              <p>Please provide a reason for rejection (optional):</p>
              <textarea
                value={rejectNotes[showRejectModal] || ''}
                onChange={(e) => setRejectNotes({ ...rejectNotes, [showRejectModal]: e.target.value })}
                placeholder="Enter rejection reason..."
                rows={4}
              />
              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowRejectModal(null)}
                  disabled={processingCredId === showRejectModal}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn reject"
                  onClick={() => handleReject(showRejectModal)}
                  disabled={processingCredId === showRejectModal}
                >
                  {processingCredId === showRejectModal ? '⏳ Rejecting...' : '❌ Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="admin-footer">
          <button className="back-btn" onClick={() => navigate('/profile')}>
            ← Back to Profile
          </button>
          <button className="refresh-btn" onClick={() => activeTab === 'users' ? fetchUsers(searchEmail, currentPage) : fetchRequests()}>
            🔄 Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
